// Compatibilité d'un plat avec le profil d'un convive — extrait de EvenementielGuestPage.tsx (2026-08-24)
// pour être partagé avec l'écran de configuration du menu côté établissement. Le but de l'extraction est
// qu'un seul et même calcul serve les deux : l'alerte annoncée à l'établissement ("aucun plat pour un
// convive sans lactose") dirait autre chose que ce que le convive voit à l'écran si la règle était
// recopiée, et les deux dériveraient au premier ajustement.
import { allergyHitsIngredient, ingredientsBlockingRestriction, keywordsForRestriction } from './allergenReference.js';

/**
 * Cible effective d'un retrait : le composant précis quand il est LUI-MÊME marqué retirable (retrait fin,
 * ex: "châtaigne" dans "Toppings"), sinon l'ingrédient parent en bloc — on ne peut pas retirer le beurre
 * d'une sauce, on retire la sauce. Idée d'Emeni (2026-08-23, deux niveaux de retirabilité le 2026-08-24).
 * Doit rester identique à removalTarget côté serveur (functions/api/[[path]].js).
 */
export const removalTargetOf = (hit) => {
    if (hit && hit.via && hit.via.removable) return { name: hit.via.name, removable: true };
    return { name: hit.ingredient.name, removable: !!hit.ingredient.removable };
};

/**
 * Ingrédients à écarter pour que ce plat convienne, déduits des restrictions ET des allergies du profil.
 * Chaque entrée : { name, removable, reason }. `reason` est un libellé lisible, à afficher tel quel.
 */
export const removalSuggestionsFor = (dish, { restrictions = [], allergies = [], customTerms = null } = {}) => {
    const ingredients = (dish && dish.ingredients) || [];
    const dietaryTags = (dish && dish.dietary_tags) || [];
    const found = new Map();
    const add = (hit, reason) => {
        const target = removalTargetOf(hit);
        if (found.has(target.name)) return;
        found.set(target.name, { name: target.name, removable: target.removable, reason });
    };
    restrictions.forEach((r) => {
        // "Restrictions couvertes" lève le repli par mots-clés (l'établissement affirme que sa viande est
        // certifiée halal), mais ne masque JAMAIS un allergène identifié par le référentiel : un plat
        // déclaré "sans lactose" à tort continue de signaler son beurre. Une case cochée par erreur ne doit
        // pas pouvoir rendre un allergène invisible.
        const covered = dietaryTags.includes(r);
        const keywords = covered ? [] : keywordsForRestriction(r, customTerms);
        ingredientsBlockingRestriction(r, ingredients, keywords, customTerms).forEach((h) => add(h, `« ${r} »`));
    });
    allergies.forEach((a) => {
        ingredients.forEach((i) => {
            const hit = allergyHitsIngredient(a, i, customTerms);
            if (hit) add(hit, `votre allergie « ${a} »`);
        });
    });
    return Array.from(found.values());
};

/**
 * État d'un plat vis-à-vis d'un profil, calculé AVANT toute sélection pour que la liste se lise d'un coup
 * d'œil : "ok" rien à signaler, "adaptable" tout ce qui gêne peut être retiré, "bloque" au moins un élément
 * gênant ne peut pas l'être — il faut alors choisir un autre plat. Demande d'Emeni (2026-08-23).
 */
export const dishStatusFor = (dish, profile) => {
    const s = removalSuggestionsFor(dish, profile);
    if (!s.length) return 'ok';
    return s.every((x) => x.removable) ? 'adaptable' : 'bloque';
};

/**
 * Ce que devient une catégorie entière (Entrée, Plat, Dessert) pour UN profil donné : combien de plats
 * passent sans rien retirer, combien seulement en retirant quelque chose, combien sont hors de portée.
 * `verdict` résume la situation du convive :
 *   'ok'        — au moins un plat lui convient tel quel ;
 *   'adaptable' — il en reste, mais il devra demander un retrait à chaque fois ;
 *   'aucun'     — impasse : plus rien à choisir dans cette catégorie.
 * Une catégorie sans plat renvoie 'ok' : il n'y a rien à couvrir, ce n'est pas un défaut de la carte.
 */
export const categoryCoverageFor = (dishes, profile) => {
    const list = Array.isArray(dishes) ? dishes : [];
    if (!list.length) return { verdict: 'ok', ok: 0, adaptable: 0, bloque: 0, total: 0 };
    let ok = 0, adaptable = 0, bloque = 0;
    list.forEach((d) => {
        const s = dishStatusFor(d, profile);
        if (s === 'ok') ok++;
        else if (s === 'adaptable') adaptable++;
        else bloque++;
    });
    const verdict = ok > 0 ? 'ok' : (adaptable > 0 ? 'adaptable' : 'aucun');
    return { verdict, ok, adaptable, bloque, total: list.length };
};
